import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import GifPicker from '@/components/GifPicker';
import GifReactionPicker from '@/components/GifReactionPicker';
import YouTubeEmbed, { containsYouTubeUrl } from '@/components/YouTubeEmbed';
import TikTokFeed from '@/components/TikTokFeed';
import MediaGallery, { MediaGalleryButton } from '@/components/MediaGallery';
import ReportModal from '@/components/ReportModal';
import CommunityNotes from '@/components/CommunityNotes';
import ImageEditor from '@/components/ImageEditor';
import LinkifiedText from '@/components/LinkifiedText';
import { AIIndicator } from '@/components/AIBadge';
import QuotedPost, { QuoteButton } from '@/components/QuotedPost';
import { ThreadBadge } from '@/components/ThreadView';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Heart, MessageCircle, Send, Image as ImageIcon, Repeat, Bookmark, MoreHorizontal, Pencil, Trash2, X, Check, BadgeCheck, Shield, Play, Layers, Sparkles, ChevronDown, ChevronUp, Smile, Youtube, Flame, TrendingUp, Eye, EyeOff, Flag, MapPin, Trophy, Bot, MessageSquareQuote, BookOpen, Sparkle, Video, Music } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import VoteForButton from '@/components/VoteForButton';
import { useAIFilter } from '@/context/AIFilterContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Global axios interceptor - attach Bearer token from localStorage
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token') || localStorage.getItem('socyy_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Feed Video Player with time limit
const FeedVideoPlayer = ({ src, isPremium, maxSeconds = 30, premiumMaxSeconds = 300, testId }) => {
  const videoRef = React.useRef(null);
  const [timeLeft, setTimeLeft] = React.useState(null);
  const [paused, setPaused] = React.useState(false);
  const limit = isPremium ? premiumMaxSeconds : maxSeconds;

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onTimeUpdate = () => {
      const elapsed = video.currentTime;
      const remaining = Math.max(0, limit - elapsed);
      setTimeLeft(Math.ceil(remaining));
      if (elapsed >= limit) {
        video.pause();
        setPaused(true);
      }
    };
    const onPlay = () => setPaused(false);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('play', onPlay);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('play', onPlay);
    };
  }, [limit]);

  return (
    <div className="relative">
      <video
        ref={videoRef}
        src={src}
        className="w-full max-h-96 object-contain"
        controls
        controlsList="nodownload"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        data-testid={testId}
      />
      {timeLeft !== null && timeLeft <= 10 && timeLeft > 0 && !paused && (
        <div className="absolute top-2 left-2 px-2 py-1 bg-black/70 rounded-full text-xs text-white font-bold" data-testid="video-time-left">
          {timeLeft}s left
        </div>
      )}
      {paused && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60" data-testid="video-limit-overlay">
          <p className="text-white font-bold text-sm mb-1">Preview limit reached</p>
          <p className="text-white/70 text-xs">{isPremium ? '5 min premium limit' : '30s preview · Upgrade for 5 min'}</p>
        </div>
      )}
    </div>
  );
};

// Post Card Component
const PostCard = ({ post, idx, user, onLike, onEdit, onDelete, onHidePost, onHideUser, onReport, onToggleComments, onShowViewers, expandedPostId, comments, loadingComments, onSubmitComment, newComment, setNewComment }) => {
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  
  const canEdit = post.user_id === user?.user_id || user?.is_admin;
  const isOwnPost = post.user_id === user?.user_id;
  const hasMedia = post.video_url || post.youtube_url || post.gif_url || post.audio_url || (post.images && post.images.length > 0);
  const hasReports = post.reports_count > 0;

  const handleSaveEdit = async () => {
    if (!editContent.trim()) return;
    await onEdit(post.post_id, editContent);
    setIsEditing(false);
  };

  const goToProfile = () => {
    navigate(`/profile/${post.user?.user_id}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.05 }}
    >
      <Card className="p-5 rounded-2xl border border-white/20 shadow-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-md hover:shadow-xl transition-all" data-testid={`post-${idx}`}>
        <div className="flex gap-4">
          <button onClick={goToProfile} className="flex-shrink-0 focus:outline-none" data-testid={`post-avatar-${idx}`}>
            <Avatar className="w-11 h-11 ring-2 ring-primary/20 hover:ring-primary transition-all cursor-pointer">
              <AvatarImage src={post.user?.avatar} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">
                {post.user?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
          </button>
          
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <button onClick={goToProfile} className="flex items-center gap-1.5 hover:underline focus:outline-none" data-testid={`post-username-${idx}`}>
                  <h3 className="font-bold text-gray-900 dark:text-white truncate">{post.user?.name}</h3>
                  {post.user?.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                  {post.user?.is_admin && <Shield className="w-4 h-4 text-red-500 flex-shrink-0" />}
                </button>
                <button onClick={goToProfile} className="text-gray-500 dark:text-gray-400 text-sm hover:underline focus:outline-none">
                  @{post.user?.username}
                </button>
              </div>
              
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Post Type Badge */}
                {post.post_type && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    post.post_type === 'video' || post.post_type === 'video_text' 
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                      : post.post_type === 'image' || post.post_type === 'image_text'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : post.post_type === 'audio' || post.post_type === 'audio_text'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      : post.post_type === 'gif' || post.post_type === 'gif_text'
                      ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`} style={post.post_type === 'video' || post.post_type === 'video_text' ? { fontFamily: "'Playfair Display', Georgia, serif", fontStyle: 'italic' } : {}} data-testid={`post-type-badge-${idx}`}>
                    {post.post_type === 'video' ? 'Vidyy' 
                      : post.post_type === 'video_text' ? 'Vidyy + Text'
                      : post.post_type === 'image' ? 'Image'
                      : post.post_type === 'image_text' ? 'Image + Text'
                      : post.post_type === 'audio' ? 'Audio'
                      : post.post_type === 'audio_text' ? 'Audio + Text'
                      : post.post_type === 'gif' ? 'GIF'
                      : post.post_type === 'gif_text' ? 'GIF + Text'
                      : 'Text'}
                  </span>
                )}
                
                {/* Engagement Score */}
                {post.engagement_score > 0 && (
                  <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-full">
                    <Flame className="w-3.5 h-3.5 text-orange-500" />
                    <span className="text-xs font-bold text-orange-600 dark:text-orange-400">{Math.round(post.engagement_score)}</span>
                  </div>
                )}
                
                {/* AI Content Indicator - Clearly visible */}
                {(post.is_ai_generated || post.ai_assisted || post.ai_disclosure) && (
                  <div 
                    className="flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-500/30" 
                    data-testid="ai-post-badge"
                    title="This content was created with AI assistance"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                    <span className="text-xs font-semibold text-violet-500 dark:text-violet-400">
                      {post.is_ai_generated ? 'AI Generated' : 'AI-Assisted'}
                    </span>
                  </div>
                )}
                
                <span className="text-xs text-gray-500">
                  {post.created_at ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : 'just now'}
                </span>
                
                {/* Menu - always show for all posts */}
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowMenu(!showMenu)}
                    className="rounded-full p-1 h-8 w-8"
                    data-testid={`post-menu-btn-${idx}`}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                  <AnimatePresence>
                    {showMenu && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="absolute right-0 top-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl border py-2 z-20 min-w-48"
                      >
                        {/* Not Interested - for posts by others */}
                        {!isOwnPost && (
                          <>
                            <button
                              onClick={() => { onHidePost(post.post_id); setShowMenu(false); }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                              data-testid={`not-interested-btn-${idx}`}
                            >
                              <X className="w-4 h-4" /> Not interested
                            </button>
                            <button
                              onClick={() => { onHideUser(post.user_id, post.user?.name); setShowMenu(false); }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                              data-testid={`hide-user-btn-${idx}`}
                            >
                              <EyeOff className="w-4 h-4" /> Don't show posts from @{post.user?.username}
                            </button>
                            <button
                              onClick={() => { onReport(post.post_id, post.user?.username); setShowMenu(false); }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                              data-testid={`report-btn-${idx}`}
                            >
                              <Flag className="w-4 h-4" /> Report post
                            </button>
                            <div className="border-t my-1"></div>
                          </>
                        )}
                        
                        {/* Edit/Delete - only for own posts or admin */}
                        {canEdit && (
                          <>
                            <button
                              onClick={() => { setIsEditing(true); setShowMenu(false); }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                            >
                              <Pencil className="w-4 h-4" /> Edit
                            </button>
                            <button
                              onClick={() => { onDelete(post.post_id); setShowMenu(false); }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" /> Delete
                            </button>
                          </>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
            
            {/* Content - clickable to open comments */}
            {isEditing ? (
              <div className="mb-3">
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="rounded-xl border resize-none mb-2"
                  rows={3}
                />
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setIsEditing(false)} className="rounded-full">
                    <X className="w-4 h-4 mr-1" /> Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveEdit} className="rounded-full bg-primary text-white">
                    <Check className="w-4 h-4 mr-1" /> Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mb-3 cursor-pointer" onClick={() => onToggleComments(post.post_id)} data-testid={`post-content-clickable-${idx}`}>
                {post.content && (
                  <p className="text-gray-800 dark:text-gray-200 mb-3 whitespace-pre-wrap">
                    <LinkifiedText 
                      text={post.content} 
                      onMentionClick={(username) => navigate(`/profile/${username}`)}
                      onHashtagClick={(tag) => navigate(`/explore?tag=${tag}`)}
                    />
                  </p>
                )}
                
                {/* YouTube Embed */}
                {(post.youtube_url || containsYouTubeUrl(post.content)) && (
                  <YouTubeEmbed url={post.youtube_url || containsYouTubeUrl(post.content)} className="mb-3 rounded-xl overflow-hidden" />
                )}
                
                {/* Video */}
                {post.video_url && !post.youtube_url && (
                  <div className="relative rounded-xl overflow-hidden bg-black mb-3">
                    <FeedVideoPlayer 
                      src={`${process.env.REACT_APP_BACKEND_URL}${post.video_url}`}
                      isPremium={user?.is_premium || user?.subscription_tier === 'pro' || user?.subscription_tier === 'creator' || user?.subscription_tier === 'supremium'}
                      maxSeconds={30}
                      premiumMaxSeconds={300}
                      testId={`post-video-${idx}`}
                    />
                    {post.views_count > 0 && (
                      <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 bg-black/60 rounded-full">
                        <Eye className="w-3 h-3 text-white" />
                        <span className="text-xs text-white">{post.views_count}</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Audio */}
                {post.audio_url && (
                  <div className="mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800" data-testid={`post-audio-${idx}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <Music className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-xs font-semibold text-green-700 dark:text-green-300">Audio</span>
                    </div>
                    <audio
                      src={`${process.env.REACT_APP_BACKEND_URL}${post.audio_url}`}
                      controls
                      className="w-full h-10"
                      preload="metadata"
                    />
                  </div>
                )}
                
                {/* GIF */}
                {post.gif_url && (
                  <img src={post.gif_url} alt="GIF" className="max-h-72 rounded-xl border screen-protected" draggable="false" onContextMenu={(e) => e.preventDefault()} data-testid={`post-gif-${idx}`} />
                )}
                
                {/* Images */}
                {post.images && post.images.length > 0 && (
                  <div className={`grid gap-2 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} mb-3`}>
                    {post.images.map((img, i) => (
                      <img key={i} src={img} alt="" className="rounded-xl w-full object-cover screen-protected" draggable="false" onContextMenu={(e) => e.preventDefault()} />
                    ))}
                  </div>
                )}
                
                {/* Quoted Post */}
                {post.is_quote_post && post.quoted_post && (
                  <QuotedPost quotedPost={post.quoted_post} />
                )}
                
                {/* Thread Indicator */}
                {post.is_thread_starter && post.thread_id && (
                  <div className="mt-3">
                    <ThreadBadge 
                      threadId={post.thread_id} 
                      postsCount={post.thread_posts_count || 1}
                      onClick={() => navigate(`/thread/${post.thread_id}`)}
                    />
                  </div>
                )}
                
                {/* Community Notes for fact-checking */}
                <CommunityNotes postId={post.post_id} hasReports={hasReports} />
              </div>
            )}
            
            {/* Actions */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onLike(post.post_id, post.liked)}
                className={`rounded-full ${post.liked ? 'text-red-500' : 'text-gray-600 hover:text-red-500'}`}
                data-testid={`like-btn-${idx}`}
              >
                <Heart className={`w-5 h-5 mr-1 ${post.liked ? 'fill-current' : ''}`} />
                <span className="font-semibold">{post.likes_count || 0}</span>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onToggleComments(post.post_id)}
                className={`rounded-full ${expandedPostId === post.post_id ? 'text-primary' : 'text-gray-600'}`}
                data-testid={`comments-btn-${idx}`}
              >
                <MessageCircle className="w-5 h-5 mr-1" />
                <span className="font-semibold">{post.comments_count || 0}</span>
                {expandedPostId === post.post_id ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
              </Button>
              
              <Button variant="ghost" size="sm" className="rounded-full text-gray-600 hover:text-accent">
                <Repeat className="w-5 h-5 mr-1" />
                <span className="font-semibold">{post.shares_count || 0}</span>
              </Button>
              
              {/* Quote Post Button */}
              <QuoteButton 
                onClick={() => navigate(`/compose?quote=${post.post_id}`)}
                quotesCount={post.quotes_count || 0}
              />
              
              {/* Vote For Awards Button */}
              <VoteForButton
                contentId={post.post_id}
                contentType="post"
                size="sm"
                isOwnContent={post.user_id === user?.user_id}
              />
              
              {/* Views Counter - clickable for post authors */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-full text-gray-500 hover:text-gray-700"
                onClick={() => {
                  if (post.user_id === user?.user_id) {
                    onShowViewers?.(post.post_id);
                  }
                }}
                data-testid={`views-btn-${idx}`}
              >
                <Eye className="w-4 h-4 mr-1" />
                <span className="text-xs">{post.views_count || 0}</span>
              </Button>
              
              <Button variant="ghost" size="sm" className="rounded-full text-gray-600 hover:text-secondary ml-auto">
                <Bookmark className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Comments Section */}
            <AnimatePresence>
              {expandedPostId === post.post_id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-4 pt-4 border-t"
                >
                  {/* Comment input */}
                  <div className="flex gap-2 mb-4">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user?.avatar} />
                      <AvatarFallback className="bg-primary text-white text-xs">{user?.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 flex gap-2">
                      <input
                        type="text"
                        placeholder="Write a comment..."
                        value={newComment[post.post_id] || ''}
                        onChange={(e) => setNewComment(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                        className="flex-1 px-4 py-2 rounded-full border bg-gray-50 dark:bg-gray-700 text-sm"
                        onKeyDown={(e) => e.key === 'Enter' && onSubmitComment(post.post_id)}
                      />
                      <Button
                        size="sm"
                        onClick={() => onSubmitComment(post.post_id)}
                        disabled={!newComment[post.post_id]?.trim()}
                        className="rounded-full"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Comments list */}
                  {loadingComments[post.post_id] ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {comments[post.post_id]?.length === 0 ? (
                        <p className="text-center text-gray-500 text-sm py-2">No comments yet</p>
                      ) : (
                        comments[post.post_id]?.map((comment, cIdx) => (
                          <div key={comment.comment_id} className="flex gap-2">
                            <Avatar className="w-7 h-7">
                              <AvatarImage src={comment.user?.avatar} />
                              <AvatarFallback className="bg-primary text-white text-xs">{comment.user?.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-xl p-2.5">
                              <div className="flex items-center gap-1.5 mb-1">
                                <span className="font-semibold text-sm">{comment.user?.name}</span>
                                {comment.user?.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-blue-500" />}
                                <span className="text-xs text-gray-500">
                                  {comment.created_at ? formatDistanceToNow(new Date(comment.created_at), { addSuffix: true }) : 'just now'}
                                </span>
                              </div>
                              <p className="text-sm">{comment.content}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

// Main Feed Component
const Feed = ({ user }) => {
  const navigate = useNavigate();
  const { hideAIContent } = useAIFilter();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [creating, setCreating] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('feedViewMode') || 'hybrid');
  const [feedType, setFeedType] = useState(() => localStorage.getItem('feedType') || 'foryou');
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [selectedGif, setSelectedGif] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedPostId, setExpandedPostId] = useState(null);
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [showMediaGallery, setShowMediaGallery] = useState(false);
  const [reportModal, setReportModal] = useState({ isOpen: false, postId: null, postAuthor: null });
  const [viewersModal, setViewersModal] = useState({ isOpen: false, postId: null, viewers: [], loading: false, totalViews: 0, uniqueViewers: 0 });
  
  // Locale attachment for posts
  const [showLocaleForm, setShowLocaleForm] = useState(false);
  const [localeData, setLocaleData] = useState(null);
  const [localeForm, setLocaleForm] = useState({
    post_type: 'event',
    address: '',
    latitude: '',
    longitude: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    price: '',
    category: '',
    ticket_url: ''
  });

  // Check if user can create locale posts (Premium Business + Admins + Supremium)
  const canCreateLocale = (user?.is_admin) || 
    (user?.subscription_tier === 'supremium') ||
    ((user?.subscription_tier === 'creator' || user?.subscription_tier === 'pro' || user?.subscription_tier === 'premium') && 
     (user?.is_business || user?.account_type === 'business'));

  useEffect(() => {
    fetchFeed();
  }, [viewMode, feedType]);

  useEffect(() => {
    localStorage.setItem('feedViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('feedType', feedType);
  }, [feedType]);

  const fetchFeed = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/feed/`, {
        params: { mode: viewMode, sort_by: 'popular', feed_type: feedType },
        withCredentials: true,
      });
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.trim() && !selectedGif && !selectedImage && !selectedVideo && !selectedAudio) return;
    setCreating(true);
    try {
      // Handle video upload separately via chunked upload
      if (selectedVideo) {
        setVideoUploading(true);
        setVideoUploadProgress(0);
        
        const file = selectedVideo.file;
        const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks
        const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
        
        // Initialize chunked upload
        const initResponse = await axios.post(
          `${API_URL}/video-upload/init-chunked`,
          {
            filename: file.name,
            file_size: file.size,
            content_type: file.type,
            total_chunks: totalChunks
          },
          { withCredentials: true }
        );
        
        const uploadId = initResponse.data.upload_id;
        
        // Upload chunks
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          
          const formData = new FormData();
          formData.append('chunk', chunk);
          
          await axios.post(
            `${API_URL}/video-upload/chunk/${uploadId}/${i}`,
            formData,
            {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );
          
          setVideoUploadProgress(((i + 1) / totalChunks) * 90);
        }
        
        // Complete upload - this creates the post
        const completeResponse = await axios.post(
          `${API_URL}/video-upload/complete-chunked`,
          {
            upload_id: uploadId,
            content: newPost || 'Check out my new Vidyy!'
          },
          { withCredentials: true }
        );
        
        setVideoUploadProgress(100);
        toast.success('Vidyy uploaded successfully!');
        
        // Add to local feed
        const videoPost = { 
          ...completeResponse.data, 
          user: user, 
          user_id: user.user_id, 
          liked: false, 
          engagement_score: 0,
          post_type: 'video',
          content: newPost || 'Check out my new Vidyy!',
          video_url: completeResponse.data.video_url,
          created_at: new Date().toISOString(),
          likes_count: 0,
          comments_count: 0,
          shares_count: 0,
          views_count: 0
        };
        setPosts([videoPost, ...posts]);
        setNewPost('');
        setSelectedVideo(null);
        setVideoUploading(false);
        setVideoUploadProgress(0);
        return;
      }

      const detectedYouTubeUrl = containsYouTubeUrl(newPost);
      
      let imageUrl = null;
      let audioUrl = null;
      
      // Upload image if selected
      if (selectedImage?.file) {
        const formData = new FormData();
        formData.append('file', selectedImage.file);
        const uploadResponse = await axios.post(
          `${API_URL}/posts/upload-image`,
          formData,
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        imageUrl = uploadResponse.data.url;
      }
      
      // Upload audio if selected
      if (selectedAudio?.file) {
        const formData = new FormData();
        formData.append('file', selectedAudio.file);
        const uploadResponse = await axios.post(
          `${API_URL}/posts/upload-audio`,
          formData,
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        audioUrl = uploadResponse.data.url;
      }
      
      // Create the regular post
      const response = await axios.post(
        `${API_URL}/posts/`,
        { 
          content: newPost, 
          images: imageUrl ? [imageUrl] : [], 
          image_url: imageUrl,
          gif_url: selectedGif?.url || null, 
          youtube_url: detectedYouTubeUrl || null,
          audio_url: audioUrl || null,
          has_locale: !!localeData
        },
        { withCredentials: true }
      );
      
      // If locale data is attached, create a locale post too
      if (localeData && canCreateLocale) {
        try {
          await axios.post(
            `${API_URL}/locale/`,
            {
              title: newPost.substring(0, 100) || 'Event',
              description: newPost,
              post_type: localeData.post_type,
              address: localeData.address,
              latitude: parseFloat(localeData.latitude) || 40.7128,
              longitude: parseFloat(localeData.longitude) || -74.0060,
              start_date: localeData.start_date || null,
              end_date: localeData.end_date || null,
              start_time: localeData.start_time || null,
              end_time: localeData.end_time || null,
              price: localeData.price || null,
              category: localeData.category || null,
              ticket_url: localeData.ticket_url || null,
              image_url: imageUrl || selectedGif?.url || null,
              tags: [],
              linked_post_id: response.data.post_id
            },
            { withCredentials: true }
          );
          toast.success('Post created with Locale!');
        } catch (localeError) {
          console.error('Failed to create locale entry');
          toast.success('Post created! (Locale link failed)');
        }
      } else {
        toast.success('Post created!');
      }
      
      const userPost = { ...response.data, user: user, user_id: user.user_id, liked: false, engagement_score: 0, locale: localeData };
      setPosts([userPost, ...posts]);
      setNewPost('');
      setSelectedGif(null);
      setSelectedImage(null);
      setSelectedVideo(null);
      setSelectedAudio(null);
      setLocaleData(null);
      setShowLocaleForm(false);
      setLocaleForm({
        post_type: 'event', address: '', latitude: '', longitude: '',
        start_date: '', end_date: '', start_time: '', end_time: '',
        price: '', category: '', ticket_url: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setCreating(false);
      setVideoUploading(false);
      setVideoUploadProgress(0);
    }
  };

  const handleLike = async (postId, liked) => {
    try {
      if (liked) {
        await axios.delete(`${API_URL}/posts/${postId}/like`, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/posts/${postId}/like`, {}, { withCredentials: true });
      }
      setPosts(posts.map(p => p.post_id === postId ? { ...p, liked: !liked, likes_count: liked ? p.likes_count - 1 : p.likes_count + 1 } : p));
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleEdit = async (postId, content) => {
    try {
      await axios.put(`${API_URL}/posts/${postId}`, { content }, { withCredentials: true });
      setPosts(posts.map(p => p.post_id === postId ? { ...p, content } : p));
      toast.success('Post updated!');
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/posts/${postId}`, { withCredentials: true });
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleHidePost = async (postId) => {
    try {
      await axios.post(`${API_URL}/feed/not-interested`, { post_id: postId }, { withCredentials: true });
      setPosts(posts.filter(p => p.post_id !== postId));
      toast.success('Post hidden from your feed', {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.delete(`${API_URL}/feed/not-interested/${postId}`, { withCredentials: true });
              fetchFeed(); // Refresh to show the post again
              toast.success('Post restored');
            } catch (e) {
              toast.error('Failed to restore post');
            }
          }
        }
      });
    } catch (error) {
      toast.error('Failed to hide post');
    }
  };

  const handleHideUser = async (userId, userName) => {
    if (!window.confirm(`Hide all posts from ${userName || 'this user'}? You can undo this in Settings.`)) return;
    try {
      await axios.post(`${API_URL}/feed/hide-user`, { user_id: userId }, { withCredentials: true });
      setPosts(posts.filter(p => p.user_id !== userId));
      toast.success(`You won't see posts from ${userName || 'this user'} anymore`, {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await axios.delete(`${API_URL}/feed/hide-user/${userId}`, { withCredentials: true });
              fetchFeed(); // Refresh to show posts again
              toast.success('User unhidden');
            } catch (e) {
              toast.error('Failed to unhide user');
            }
          }
        }
      });
    } catch (error) {
      toast.error('Failed to hide user');
    }
  };

  const handleReport = (postId, postAuthor) => {
    setReportModal({ isOpen: true, postId, postAuthor });
  };

  const loadComments = async (postId) => {
    if (loadingComments[postId]) return;
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}/comments`, { withCredentials: true });
      setComments(prev => ({ ...prev, [postId]: response.data }));
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const toggleComments = (postId) => {
    if (expandedPostId === postId) {
      setExpandedPostId(null);
    } else {
      setExpandedPostId(postId);
      if (!comments[postId]) loadComments(postId);
    }
  };

  const submitComment = async (postId) => {
    const content = newComment[postId]?.trim();
    if (!content) return;
    try {
      const response = await axios.post(`${API_URL}/posts/${postId}/comments`, { content }, { withCredentials: true });
      setComments(prev => ({ ...prev, [postId]: [response.data, ...(prev[postId] || [])] }));
      setPosts(posts.map(p => p.post_id === postId ? { ...p, comments_count: p.comments_count + 1 } : p));
      setNewComment(prev => ({ ...prev, [postId]: '' }));
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleMediaSelect = (media) => {
    setShowMediaGallery(false);
    toast.info(`Viewing post by ${media.user?.name}`);
  };

  // Track post view
  const trackView = useCallback(async (postId) => {
    try {
      await axios.post(`${API_URL}/posts/${postId}/view`, {}, { withCredentials: true });
    } catch (err) {
      // Silent fail for view tracking
    }
  }, []);

  // Track views when posts load
  useEffect(() => {
    if (posts.length > 0) {
      posts.slice(0, 5).forEach(p => trackView(p.post_id));
    }
  }, [posts.length]);

  // Show viewers modal (for post authors)
  const showViewers = async (postId) => {
    setViewersModal({ isOpen: true, postId, viewers: [], loading: true, totalViews: 0, uniqueViewers: 0 });
    try {
      const response = await axios.get(`${API_URL}/posts/${postId}/viewers`, { withCredentials: true });
      setViewersModal(prev => ({
        ...prev,
        viewers: response.data.viewers || [],
        totalViews: response.data.total_views || 0,
        uniqueViewers: response.data.unique_viewers || 0,
        loading: false
      }));
    } catch (error) {
      if (error.response?.status === 403) {
        toast.info('Only the post author can see who viewed this post');
      } else {
        toast.error('Failed to load viewers');
      }
      setViewersModal(prev => ({ ...prev, isOpen: false, loading: false }));
    }
  };

  // Video Mode - TikTok style full screen
  if (viewMode === 'video') {
    return (
      <div className="h-screen" data-testid="video-mode">
        {/* Mode Toggle - Fixed at top */}
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 p-1 bg-black/40 backdrop-blur-md rounded-full">
          <button
            onClick={() => setViewMode('hybrid')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${viewMode === 'hybrid' ? 'bg-white text-black' : 'text-white hover:bg-white/20'}`}
          >
            <Layers className="w-4 h-4 inline mr-1" /> Hybrid
          </button>
          <button
            onClick={() => setViewMode('video')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${viewMode === 'video' ? 'bg-white text-black' : 'text-white hover:bg-white/20'}`}
          >
            <Play className="w-4 h-4 inline mr-1" /> Videos
          </button>
        </div>
        
        <TikTokFeed user={user} />
      </div>
    );
  }

  // Hybrid Mode - Traditional feed with media gallery
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-pink-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900 pb-32" data-testid="feed-page">
      <div className="max-w-7xl mx-auto px-4 pt-6">
        <div className="flex gap-6">
          {/* Media Gallery Sidebar - Desktop */}
          <MediaGallery 
            isOpen={showMediaGallery}
            onClose={() => setShowMediaGallery(false)}
            onSelectMedia={handleMediaSelect}
            isMobile={false}
          />

          {/* Main Feed */}
          <div className="flex-1 max-w-2xl mx-auto">
            {/* Header with Feed Type & Mode Toggle */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
              {/* For You / Following Tabs - Like X/Twitter */}
              <div className="flex items-center justify-center mb-4">
                <div className="flex bg-white/80 dark:bg-gray-800/80 rounded-full p-1 shadow-lg border">
                  <button
                    onClick={() => setFeedType('foryou')}
                    className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                      feedType === 'foryou' 
                        ? 'bg-gradient-to-r from-primary to-accent text-white shadow-md' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                    }`}
                    data-testid="feed-foryou"
                  >
                    <Flame className="w-4 h-4 inline mr-1.5" />
                    For You
                  </button>
                  <button
                    onClick={() => setFeedType('following')}
                    className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                      feedType === 'following' 
                        ? 'bg-gradient-to-r from-primary to-accent text-white shadow-md' 
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'
                    }`}
                    data-testid="feed-following"
                  >
                    <Heart className="w-4 h-4 inline mr-1.5" />
                    Following
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-heading font-black text-gray-900 dark:text-white flex items-center gap-2">
                    {feedType === 'foryou' ? (
                      <>
                        <TrendingUp className="w-7 h-7 text-primary" />
                        Trending
                      </>
                    ) : (
                      <>
                        <Heart className="w-7 h-7 text-pink-500" />
                        Your Circle
                      </>
                    )}
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {feedType === 'foryou' 
                      ? 'Posts ranked by engagement across the platform' 
                      : 'Latest posts from people you follow'}
                  </p>
                </div>
                
                {/* Content Type Toggle */}
                <div className="flex items-center gap-2">
                  {/* Locale Button */}
                  <button
                    onClick={() => navigate('/locale')}
                    className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-lg hover:opacity-90 transition-all flex items-center gap-1"
                    data-testid="locale-btn"
                  >
                    <MapPin className="w-3.5 h-3.5" /> Locale
                  </button>
                  
                  <div className="flex items-center gap-1 p-1 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-lg border">
                    <button
                      onClick={() => setViewMode('hybrid')}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${viewMode === 'hybrid' ? 'bg-primary text-white shadow-md' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'}`}
                      data-testid="mode-hybrid"
                    >
                      <Layers className="w-3.5 h-3.5 inline mr-1" /> All
                    </button>
                    <button
                      onClick={() => setViewMode('video')}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${viewMode === 'video' ? 'bg-primary text-white shadow-md' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10'}`}
                      data-testid="mode-video"
                    >
                      <Play className="w-3.5 h-3.5 inline mr-1" /> Videos
                    </button>
                  </div>
                </div>
              </div>

              {/* Create Post Card */}
              <Card className="p-5 rounded-2xl border border-white/20 shadow-xl bg-white/80 dark:bg-gray-800/80 backdrop-blur-md mb-6" data-testid="create-post-card">
                <div className="flex gap-4">
                  <Avatar className="w-11 h-11 ring-2 ring-primary/20">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-bold">{user?.name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <Textarea
                      placeholder="What's happening?"
                      value={newPost}
                      onChange={(e) => setNewPost(e.target.value)}
                      className="rounded-xl border-2 resize-none mb-3 bg-gray-50 dark:bg-gray-700"
                      rows={3}
                      data-testid="create-post-input"
                    />
                    
                    {/* YouTube Preview */}
                    {containsYouTubeUrl(newPost) && (
                      <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
                        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-2">
                          <Youtube className="w-5 h-5" />
                          <span className="text-sm font-medium">YouTube video will be embedded</span>
                        </div>
                        <YouTubeEmbed url={containsYouTubeUrl(newPost)} />
                      </div>
                    )}
                    
                    {/* Selected GIF */}
                    {selectedGif && (
                      <div className="relative mb-3 inline-block">
                        <img src={selectedGif.url} alt="GIF" className="max-h-40 rounded-xl border" />
                        <button onClick={() => setSelectedGif(null)} className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    
                    {/* Selected Image */}
                    {selectedImage && !showImageEditor && (
                      <div className="relative mb-3 inline-block">
                        <img src={selectedImage.preview} alt="Selected" className="max-h-40 rounded-xl border" />
                        <button 
                          onClick={() => setSelectedImage(null)} 
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                          data-testid="remove-image-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => setShowImageEditor(true)} 
                          className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded-lg hover:bg-black/90 transition"
                          data-testid="edit-image-btn"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                    {selectedImage && showImageEditor && (
                      <div className="mb-3">
                        <ImageEditor
                          imageUrl={selectedImage.preview}
                          onSave={(editedDataUrl) => {
                            // Convert data URL to file
                            fetch(editedDataUrl).then(res => res.blob()).then(blob => {
                              const file = new File([blob], 'edited_image.jpg', { type: 'image/jpeg' });
                              setSelectedImage({ file, preview: editedDataUrl });
                              setShowImageEditor(false);
                            });
                          }}
                          onCancel={() => setShowImageEditor(false)}
                        />
                      </div>
                    )}
                    
                    {/* Selected Video */}
                    {selectedVideo && (
                      <div className="relative mb-3">
                        <div className="rounded-xl overflow-hidden bg-black">
                          <video src={selectedVideo.preview} className="w-full max-h-40 object-contain" controls controlsList="nodownload" disablePictureInPicture data-testid="selected-video-preview" />
                        </div>
                        <button 
                          onClick={() => setSelectedVideo(null)} 
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full z-10"
                          data-testid="remove-video-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        {videoUploading && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                              <span>Uploading Vidyy...</span>
                              <span>{videoUploadProgress.toFixed(0)}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                                style={{ width: `${videoUploadProgress}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Selected Audio */}
                    {selectedAudio && (
                      <div className="relative mb-3 p-3 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-200 dark:border-green-800">
                        <button 
                          onClick={() => setSelectedAudio(null)} 
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full z-10"
                          data-testid="remove-audio-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-2 mb-2">
                          <Music className="w-4 h-4 text-green-600" />
                          <span className="text-xs font-semibold text-green-700 dark:text-green-300">{selectedAudio.file.name}</span>
                        </div>
                        <audio src={selectedAudio.preview} controls className="w-full h-8" data-testid="selected-audio-preview" />
                      </div>
                    )}
                    
                    {/* Locale Attachment Badge */}
                    {localeData && (
                      <div className="mb-3 p-3 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-orange-500" />
                            <div>
                              <span className="text-sm font-semibold text-orange-700 dark:text-orange-300">
                                {localeData.post_type.charAt(0).toUpperCase() + localeData.post_type.slice(1)} at {localeData.address}
                              </span>
                              {localeData.start_date && (
                                <p className="text-xs text-orange-600 dark:text-orange-400">
                                  {localeData.start_date} {localeData.start_time && `at ${localeData.start_time}`}
                                </p>
                              )}
                            </div>
                          </div>
                          <button 
                            onClick={() => { setLocaleData(null); setShowLocaleForm(false); }}
                            className="p-1 hover:bg-orange-200 dark:hover:bg-orange-800 rounded-full"
                          >
                            <X className="w-4 h-4 text-orange-500" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* Locale Form */}
                    <AnimatePresence>
                      {showLocaleForm && !localeData && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="mb-3 p-4 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 rounded-xl border border-orange-200 dark:border-orange-800 overflow-hidden"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-orange-700 dark:text-orange-300 flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              Add Locale Details
                            </h4>
                            <button onClick={() => setShowLocaleForm(false)} className="text-gray-500 hover:text-gray-700">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                            {['event', 'show', 'special', 'deal'].map(type => (
                              <button
                                key={type}
                                onClick={() => setLocaleForm({ ...localeForm, post_type: type })}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                  localeForm.post_type === type 
                                    ? type === 'event' ? 'bg-blue-500 text-white' 
                                      : type === 'show' ? 'bg-purple-500 text-white'
                                      : type === 'special' ? 'bg-orange-500 text-white'
                                      : 'bg-green-500 text-white'
                                    : 'bg-white dark:bg-gray-700 border'
                                }`}
                              >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                              </button>
                            ))}
                          </div>
                          
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={localeForm.address}
                              onChange={(e) => setLocaleForm({ ...localeForm, address: e.target.value })}
                              placeholder="Address *"
                              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                            />
                            
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="number"
                                step="0.0001"
                                value={localeForm.latitude}
                                onChange={(e) => setLocaleForm({ ...localeForm, latitude: e.target.value })}
                                placeholder="Latitude"
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                              <input
                                type="number"
                                step="0.0001"
                                value={localeForm.longitude}
                                onChange={(e) => setLocaleForm({ ...localeForm, longitude: e.target.value })}
                                placeholder="Longitude"
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="date"
                                value={localeForm.start_date}
                                onChange={(e) => setLocaleForm({ ...localeForm, start_date: e.target.value })}
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                              <input
                                type="time"
                                value={localeForm.start_time}
                                onChange={(e) => setLocaleForm({ ...localeForm, start_time: e.target.value })}
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={localeForm.price}
                                onChange={(e) => setLocaleForm({ ...localeForm, price: e.target.value })}
                                placeholder="Price (Free, $10, etc.)"
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              />
                              <select
                                value={localeForm.category}
                                onChange={(e) => setLocaleForm({ ...localeForm, category: e.target.value })}
                                className="px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                              >
                                <option value="">Category</option>
                                <option value="music">Music</option>
                                <option value="food">Food & Dining</option>
                                <option value="sports">Sports</option>
                                <option value="art">Art & Culture</option>
                                <option value="nightlife">Nightlife</option>
                                <option value="shopping">Shopping</option>
                                <option value="fitness">Fitness</option>
                                <option value="tech">Tech</option>
                                <option value="community">Community</option>
                              </select>
                            </div>
                            
                            <input
                              type="url"
                              value={localeForm.ticket_url}
                              onChange={(e) => setLocaleForm({ ...localeForm, ticket_url: e.target.value })}
                              placeholder="Ticket/Booking URL (optional)"
                              className="w-full px-3 py-2 rounded-lg border text-sm bg-white dark:bg-gray-700"
                            />
                          </div>
                          
                          <div className="flex justify-end gap-2 mt-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowLocaleForm(false)}
                              className="rounded-full"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                if (!localeForm.address) {
                                  toast.error('Please enter an address');
                                  return;
                                }
                                setLocaleData({ ...localeForm });
                                setShowLocaleForm(false);
                              }}
                              className="rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white"
                              data-testid="attach-locale-btn"
                            >
                              <MapPin className="w-3 h-3 mr-1" />
                              Attach Location
                            </Button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    
                    <div className="flex flex-col gap-3">
                      {/* Media Type Selector Bar */}
                      <div className="flex items-center gap-2 flex-wrap" data-testid="media-buttons-bar">
                        <label className="cursor-pointer" data-testid="image-btn-label">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setSelectedVideo(null);
                                setSelectedAudio(null);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setSelectedImage({ file, preview: reader.result });
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="sr-only"
                            data-testid="image-upload-input"
                          />
                          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer border-2 ${selectedImage ? 'bg-blue-500 text-white border-blue-500 shadow-lg shadow-blue-500/25' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-blue-400 hover:text-blue-500'}`} data-testid="image-upload-btn">
                            <ImageIcon className="w-4 h-4" />
                            <span>Image</span>
                          </div>
                        </label>
                        <label className="cursor-pointer" data-testid="video-btn-label">
                          <input
                            type="file"
                            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 500 * 1024 * 1024) {
                                  toast.error('Video too large. Maximum size is 500MB.');
                                  return;
                                }
                                setSelectedImage(null);
                                setSelectedAudio(null);
                                setSelectedVideo({ file, preview: URL.createObjectURL(file) });
                              }
                            }}
                            className="sr-only"
                            data-testid="video-upload-input"
                          />
                          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer border-2 ${selectedVideo ? 'bg-purple-500 text-white border-purple-500 shadow-lg shadow-purple-500/25' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-purple-400 hover:text-purple-500'}`} data-testid="video-upload-btn">
                            <Video className="w-4 h-4" />
                            <span>Vidyy</span>
                          </div>
                        </label>
                        <label className="cursor-pointer" data-testid="audio-btn-label">
                          <input
                            type="file"
                            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/aac,audio/flac,audio/x-m4a,audio/mp4,audio/webm"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 50 * 1024 * 1024) {
                                  toast.error('Audio too large. Maximum size is 50MB.');
                                  return;
                                }
                                setSelectedImage(null);
                                setSelectedVideo(null);
                                setSelectedAudio({ file, preview: URL.createObjectURL(file) });
                              }
                            }}
                            className="sr-only"
                            data-testid="audio-upload-input"
                          />
                          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer border-2 ${selectedAudio ? 'bg-green-500 text-white border-green-500 shadow-lg shadow-green-500/25' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-green-400 hover:text-green-500'}`} data-testid="audio-upload-btn">
                            <Music className="w-4 h-4" />
                            <span>Audio</span>
                          </div>
                        </label>
                        <div className="relative">
                          <div 
                            onClick={() => setShowGifPicker(!showGifPicker)} 
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer border-2 ${selectedGif ? 'bg-pink-500 text-white border-pink-500 shadow-lg shadow-pink-500/25' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-pink-400 hover:text-pink-500'}`}
                            data-testid="gif-picker-btn"
                          >
                            <Sparkles className="w-4 h-4" />
                            <span>GIF</span>
                          </div>
                          <AnimatePresence>
                            {showGifPicker && <GifPicker onSelect={(gif) => { setSelectedGif(gif); setShowGifPicker(false); }} onClose={() => setShowGifPicker(false)} />}
                          </AnimatePresence>
                        </div>
                        
                        {/* Locale Button - Only for verified businesses */}
                        {canCreateLocale && (
                          <div
                            onClick={() => setShowLocaleForm(!showLocaleForm)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all cursor-pointer border-2 ${localeData ? 'bg-orange-500 text-white border-orange-500 shadow-lg shadow-orange-500/25' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:border-orange-400 hover:text-orange-500'}`}
                            data-testid="locale-attach-btn"
                          >
                            <MapPin className="w-4 h-4" />
                            <span>Locale</span>
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end">
                      <Button
                        onClick={createPost}
                        disabled={creating || videoUploading || (!newPost.trim() && !selectedGif && !selectedImage && !selectedVideo && !selectedAudio)}
                        className="rounded-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white font-bold px-6 shadow-lg"
                        data-testid="create-post-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {videoUploading ? 'Uploading...' : creating ? 'Sending...' : 'Send it!'}
                      </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* AI Filter Active Banner */}
            {hideAIContent && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-500/30 rounded-xl flex items-center justify-center gap-2"
                data-testid="ai-filter-banner"
              >
                <Sparkle className="w-4 h-4 text-violet-500" />
                <span className="text-sm font-medium text-violet-700 dark:text-violet-300">
                  AI Content Hidden - Showing only human-created posts
                </span>
              </motion.div>
            )}

            {/* Posts Feed */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : posts.length === 0 ? (
              <Card className="p-12 rounded-2xl text-center bg-white/80 dark:bg-gray-800/80">
                {feedType === 'following' ? (
                  <>
                    <Heart className="w-16 h-16 mx-auto mb-4 text-pink-300" />
                    <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">Your feed is empty</h3>
                    <p className="text-gray-500 mb-4">Follow some people to see their posts here!</p>
                    <Button onClick={() => setFeedType('foryou')} className="rounded-full bg-gradient-to-r from-primary to-accent text-white">
                      <Flame className="w-4 h-4 mr-2" /> Explore For You
                    </Button>
                  </>
                ) : (
                  <>
                    <Flame className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">No posts yet</h3>
                    <p className="text-gray-500">Be the first to post something amazing!</p>
                  </>
                )}
              </Card>
            ) : (
              <div className="space-y-4">
                {posts
                  .filter(post => {
                    // Filter out AI content if hideAIContent is enabled
                    if (hideAIContent) {
                      return !post.is_ai_generated && !post.ai_assisted && !post.ai_disclosure;
                    }
                    return true;
                  })
                  .map((post, idx) => (
                  <PostCard
                    key={post.post_id}
                    post={post}
                    idx={idx}
                    user={user}
                    onLike={handleLike}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onHidePost={handleHidePost}
                    onHideUser={handleHideUser}
                    onReport={handleReport}
                    onToggleComments={toggleComments}
                    onShowViewers={showViewers}
                    expandedPostId={expandedPostId}
                    comments={comments}
                    loadingComments={loadingComments}
                    onSubmitComment={submitComment}
                    newComment={newComment}
                    setNewComment={setNewComment}
                  />
                ))}
                {/* Show message if all posts filtered out */}
                {hideAIContent && posts.filter(p => !p.is_ai_generated && !p.ai_assisted && !p.ai_disclosure).length === 0 && posts.length > 0 && (
                  <Card className="p-8 rounded-2xl text-center bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800">
                    <Sparkle className="w-12 h-12 mx-auto mb-3 text-violet-400" />
                    <h3 className="text-lg font-bold text-violet-700 dark:text-violet-300 mb-2">All visible posts are AI-assisted</h3>
                    <p className="text-violet-600 dark:text-violet-400 text-sm">Turn off the AI filter to see these posts.</p>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <ReportModal
        isOpen={reportModal.isOpen}
        onClose={() => setReportModal({ isOpen: false, postId: null, postAuthor: null })}
        postId={reportModal.postId}
        postAuthor={reportModal.postAuthor}
      />

      {/* Mobile Media Gallery Button & Modal */}
      <MediaGalleryButton onClick={() => setShowMediaGallery(true)} />
      <MediaGallery
        isOpen={showMediaGallery}
        onClose={() => setShowMediaGallery(false)}
        onSelectMedia={handleMediaSelect}
        isMobile={true}
      />

      <FloatingDock user={user} />

      {/* Viewers Modal */}
      <AnimatePresence>
        {viewersModal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setViewersModal(prev => ({ ...prev, isOpen: false }))}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[70vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              data-testid="viewers-modal"
            >
              <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg">Post Viewers</h3>
                  <p className="text-xs text-gray-500">
                    {viewersModal.totalViews} total views · {viewersModal.uniqueViewers} unique viewers
                  </p>
                </div>
                <button onClick={() => setViewersModal(prev => ({ ...prev, isOpen: false }))} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-y-auto max-h-[50vh] p-4 space-y-3">
                {viewersModal.loading ? (
                  <div className="text-center py-8 text-gray-500">Loading viewers...</div>
                ) : viewersModal.viewers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No tracked viewers yet</div>
                ) : (
                  viewersModal.viewers.map((v, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800" data-testid={`viewer-${i}`}>
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={v.viewer?.avatar_url} />
                        <AvatarFallback className="bg-primary/10 text-primary font-bold">
                          {v.viewer?.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-sm truncate">{v.viewer?.name || 'Unknown'}</span>
                          {v.viewer?.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                        </div>
                        <span className="text-xs text-gray-500">@{v.viewer?.username || 'user'}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs text-gray-400 block">{v.view_count}x viewed</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Feed;
